/**
 * App Version Duplicate Dialog Component
 *
 * Dialog for duplicating an existing app version as a template for a new version.
 * Pre-fills form with source version data and requires new version number.
 */

import { useState } from 'react';
import { Copy, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateAppVersion } from '@/hooks/use-app-versions';
import { useToast } from '@/hooks/use-toast';
import type { AppVersion, Platform } from '@/types/app-version';

interface AppVersionDuplicateDialogProps {
  version: AppVersion | null;
  onClose: () => void;
}

/**
 * Increment Version
 * Suggests next version number by incrementing patch version
 */
function suggestNextVersion(currentVersion: string): string {
  const parts = currentVersion.split('.');
  if (parts.length === 3) {
    const patch = parseInt(parts[2], 10);
    if (!isNaN(patch)) {
      return `${parts[0]}.${parts[1]}.${patch + 1}`;
    }
  }
  return currentVersion;
}

/**
 * App Version Duplicate Dialog
 * Renders dialog for cloning a version with new version number
 */
export function AppVersionDuplicateDialog({ version, onClose }: AppVersionDuplicateDialogProps) {
  const { toast } = useToast();
  const [isCreating, setIsCreating] = useState(false);
  const [newVersion, setNewVersion] = useState('');
  const [newPlatform, setNewPlatform] = useState<Platform>('both');
  const createAppVersionMutation = useCreateAppVersion();

  // Initialize form when version changes
  const suggestedVersion = version ? suggestNextVersion(version.version) : '';

  const handleDuplicate = async () => {
    if (!version || !newVersion) return;

    setIsCreating(true);
    try {
      await createAppVersionMutation.mutateAsync({
        // Copy all fields from source version
        appBundleId: version.appBundleId,
        version: newVersion,
        platform: newPlatform,
        isMandatory: version.isMandatory,
        priority: version.priority,
        minSupportedVersion: version.minSupportedVersion || undefined,
        storeUrlIos: version.storeUrlIos || undefined,
        storeUrlAndroid: version.storeUrlAndroid || undefined,
        messageImageUrl: version.messageImageUrl || undefined,
        messageTitle: version.messageTitle || undefined,
        messageDescription: version.messageDescription || undefined,
        mandatoryMessage: version.mandatoryMessage || undefined,
        actionButtonText: version.actionButtonText,
        dismissButtonText: version.dismissButtonText || undefined,
        releaseNotes: version.releaseNotes || undefined,
        metadata: version.metadata || undefined,
        isActive: false, // Start as inactive for review
        isBeta: version.isBeta,
        // Do NOT copy: id, forceUpdateAfter, releaseDate, createdAt, updatedAt, createdBy
      });

      toast({
        title: 'Version Duplicated',
        description: `New version ${newVersion} created based on ${version.version}`,
      });

      onClose();
      setNewVersion('');
      setNewPlatform('both');
    } catch (error) {
      toast({
        title: 'Duplication Failed',
        description: error instanceof Error ? error.message : 'Failed to duplicate version',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Reset form when dialog opens
  const handleOpenChange = (open: boolean) => {
    if (!open && !isCreating) {
      onClose();
      setNewVersion('');
      setNewPlatform('both');
    }
  };

  return (
    <Dialog open={version !== null} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Duplicate App Version
          </DialogTitle>
          <DialogDescription>
            Create a new version based on {version?.version}. The new version will inherit all
            settings but will be created as inactive.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Source Version Info */}
          <div className="bg-muted p-4 rounded-md space-y-2">
            <p className="text-sm font-medium">Source Version</p>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Version:</span>
              <span className="font-mono font-medium">{version?.version}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Platform:</span>
              <span>{version?.platform.toUpperCase()}</span>
            </div>
          </div>

          {/* New Version Number */}
          <div className="space-y-2">
            <Label htmlFor="newVersion">New Version Number *</Label>
            <Input
              id="newVersion"
              placeholder={suggestedVersion}
              value={newVersion}
              onChange={e => setNewVersion(e.target.value)}
              disabled={isCreating}
            />
            <p className="text-xs text-muted-foreground">Suggested: {suggestedVersion}</p>
          </div>

          {/* Platform Selection */}
          <div className="space-y-2">
            <Label htmlFor="platform">Platform</Label>
            <Select
              value={newPlatform}
              onValueChange={value => setNewPlatform(value as Platform)}
              disabled={isCreating}
            >
              <SelectTrigger id="platform">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ios">iOS</SelectItem>
                <SelectItem value="android">Android</SelectItem>
                <SelectItem value="both">Both</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <p className="text-sm text-muted-foreground">
            All other settings (priority, messages, store URLs, etc.) will be copied from the source
            version. You can edit them after creation.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleDuplicate} disabled={isCreating || !newVersion}>
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-2" />
                Create Duplicate
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
